import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography as MuiTypography,
  styled,
  useTheme,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import {
  useStyles as tileContentStyles,
  ScrollableContainer,
  StyledSwatchDiv,
  StyledSwatchDivWithBackground,
} from "../product-title-content/ProductTileContentStyles";
import swatchIcon from "../../assets/color-swatch-icon.svg";
import { CDN_WIDGET_URL, removeDotFromStart } from "../../utils/WidgetUtil";
import { isAbsolutePath } from "../../utils/ProductUtil";
import { ColorData } from "../../store/color-management/ColorManagementTypes";

export function colorMatchesProductTileStripRow(color: ColorData): boolean {
  return color.thumbnail !== "" && color.swatchImage !== "";
}

const SwatchStripRoot = styled(StyledSwatchDiv)`
  padding-top: 0 !important;
`;

interface Props {
  productId: string;
  publishedColors: ColorData[];
  typeCode?: string;
  hasColors: boolean;
  selectedColorName: string;
  onColorChange: (thumbnail: string, name: string, colorId: string) => void;
  productImagePrefix: string;
  inventoryThreshold: number;
  maxVisible?: number;
  hideLabel?: boolean;
}

const ProductColorSwatchStrip: React.FC<Props> = ({
  productId,
  publishedColors,
  typeCode,
  hasColors,
  selectedColorName,
  onColorChange,
  productImagePrefix,
  inventoryThreshold,
  maxVisible = 4,
  hideLabel = false,
}) => {
  const theme = useTheme();
  const classes = tileContentStyles(theme);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const stripColorsCount =
    publishedColors?.filter(colorMatchesProductTileStripRow).length ?? 0;

  const shouldRender =
    hasColors &&
    stripColorsCount > 0 &&
    typeCode !== "ProductSet" &&
    typeCode !== "ProductBundle";

  const handleColorClick = useCallback(
    (
      event: React.MouseEvent,
      thumbnail: string,
      name: string,
      colorId: string,
    ) => {
      event.stopPropagation();
      onColorChange(thumbnail, name, colorId);
    },
    [onColorChange],
  );

  const handleOpenMore = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback((event: unknown) => {
    (event as { stopPropagation?: () => void })?.stopPropagation?.();
    setAnchorEl(null);
  }, []);

  const renderSwatchTile = useCallback(
    (color: ColorData) => {
      const isSelected = color.name === selectedColorName;
      let borderClass: Record<string, unknown>;
      if (color.stock === 0) {
        borderClass = classes.redBorder(isSelected);
      } else if (color.stock <= inventoryThreshold) {
        borderClass = classes.yellowBorder(isSelected);
      } else if (isSelected) {
        borderClass = classes.selectedBoxStyle;
      } else {
        borderClass = classes.defaultBorder;
      }

      const swatchClass = {
        ...classes.colorSwatchImageContainer,
        ...borderClass,
      };
      // Same as ProductTileContent `colorSwatches`: circle uses swatch asset first, then thumbnail.
      const productColorSwatch = color.swatchImage || color.thumbnail;
      const swatchImg = isAbsolutePath(productColorSwatch)
        ? productColorSwatch
        : productImagePrefix + productColorSwatch;
      const useCssBackground = !color.swatchImage && Boolean(color.cssColor);

      // Main tile image uses color thumbnail (same as ProductTileContent handleColorSwatchName).
      return (
        <Box style={classes.swatch} key={`${color.code}-${color.colorId}`}>
          <div
            onClick={(event) =>
              handleColorClick(
                event,
                color.thumbnail,
                color.name,
                color.colorId,
              )
            }
            title={color.name}
          >
            <div style={swatchClass as React.CSSProperties}>
              {useCssBackground ? (
                <StyledSwatchDivWithBackground color={color.cssColor ?? ""} />
              ) : (
                <img
                  src={swatchImg}
                  alt={color.code}
                  style={classes.colorSwatchImage}
                />
              )}
            </div>
          </div>
        </Box>
      );
    },
    [
      classes,
      handleColorClick,
      inventoryThreshold,
      productImagePrefix,
      selectedColorName,
    ],
  );

  const { visibleTiles, overflowTiles, popoverId } = useMemo(() => {
    const popId = `product-more-swatches-${productId}`;
    const visible: React.ReactNode[] = [];
    const overflow: React.ReactNode[] = [];
    const stripColors =
      publishedColors?.filter(colorMatchesProductTileStripRow) ?? [];
    stripColors.forEach((color, idx) => {
      if (idx < maxVisible) {
        visible.push(renderSwatchTile(color));
      } else {
        overflow.push(renderSwatchTile(color));
      }
    });
    return { visibleTiles: visible, overflowTiles: overflow, popoverId: popId };
  }, [publishedColors, renderSwatchTile, maxVisible, productId]);

  if (!shouldRender) {
    return null;
  }

  return (
    <SwatchStripRoot>
      {!hideLabel && selectedColorName !== "" && (
        <MuiTypography variant="body3">
          Color: {selectedColorName}
        </MuiTypography>
      )}
      <ScrollableContainer>
        {visibleTiles}
        {overflowTiles.length > 0 && (
          <>
            <div
              id={popoverId}
              aria-describedby={popoverId}
              style={classes.swatchButton}
              onClick={handleOpenMore}
            >
              <img
                src={`${CDN_WIDGET_URL}${removeDotFromStart(swatchIcon)}`}
                alt=""
              />
            </div>
            <Popover
              open={Boolean(anchorEl)}
              onClose={handleClosePopover}
              id={popoverId}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              anchorEl={anchorEl}
              sx={classes.popoverStyles}
            >
              <div style={classes.swatchBox}>{overflowTiles}</div>
            </Popover>
          </>
        )}
      </ScrollableContainer>
    </SwatchStripRoot>
  );
};

export default ProductColorSwatchStrip;
